import React from 'react';
import { Award, Users, Clock, Shield } from 'lucide-react';

const AboutSection = () => {
  const features = [
    {
      icon: Clock,
      title: "Decades of Experience",
      description: "Our team brings tens of years of door production expertise to every project."
    },
    {
      icon: Users,
      title: "Expert Team", 
      description: "Skilled professionals whose shared knowledge drives our enterprise success."
    },
    {
      icon: Shield,
      title: "Quality Assurance",
      description: "Every door meets our rigorous standards for durability and performance."
    },
    {
      icon: Award,
      title: "Trusted Brand",
      description: "Established reputation for excellence in exterior door manufacturing."
    }
  ];

  const sectionStyle = {
    padding: '5rem 1.5rem',
    position: 'relative'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '4rem'
  };

  const titleStyle = {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '1.5rem'
  };

  const highlightStyle = {
    color: '#dfa926'
  };

  const descriptionStyle = {
    fontSize: '1.25rem',
    color: '#d1d5db',
    maxWidth: '48rem',
    margin: '0 auto',
    lineHeight: '1.6'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '2rem',
    marginBottom: '4rem'
  };

  const featureCardStyle = {
    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(17, 17, 17, 0.5) 100%)',
    padding: '2rem',
    borderRadius: '0.75rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(58, 58, 58, 0.5)',
    transition: 'all 0.3s ease'
  };

  const iconStyle = {
    width: '3rem',
    height: '3rem',
    color: '#dfa926',
    marginBottom: '1.5rem',
    transition: 'transform 0.3s ease'
  };

  const featureTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: 'white',
    marginBottom: '1rem'
  };

  const featureDescStyle = {
    color: '#9ca3af',
    lineHeight: '1.6'
  };

  const whyChooseStyle = {
    background: 'linear-gradient(90deg, rgba(26, 26, 26, 0.3) 0%, rgba(17, 17, 17, 0.3) 100%)',
    borderRadius: '1rem',
    padding: '3rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(58, 58, 58, 0.3)'
  };

  const whyChooseGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    alignItems: 'center'
  };

  const whyChooseTitleStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '1.5rem'
  };

  const listStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0
  };

  const listItemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '1rem',
    color: '#d1d5db'
  };

  const bulletStyle = {
    width: '0.5rem',
    height: '0.5rem',
    backgroundColor: '#dfa926',
    borderRadius: '50%',
    marginTop: '0.5rem',
    marginRight: '1rem',
    flexShrink: 0
  };

  const imageStyle = {
    borderRadius: '0.75rem',
    width: '100%',
    height: '16rem',
    objectFit: 'cover',
    background: 'linear-gradient(45deg, #3a3a3a, #1a1a1a)'
  };

  return (
    <section style={sectionStyle}>
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            About <span style={highlightStyle}>Hestia Doors</span>
          </h2>
          <p style={descriptionStyle}>
            The Hestia Doors team consists of people who have been engaged in door production 
            for tens of years, whose shared experience and knowledge have now been applied 
            to the success of our enterprise.
          </p>
        </div>

        <div style={gridStyle}>
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div 
                key={index}
                style={featureCardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(223, 169, 38, 0.5)';
                  const icon = e.currentTarget.querySelector('.feature-icon');
                  if (icon) icon.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(58, 58, 58, 0.5)';
                  const icon = e.currentTarget.querySelector('.feature-icon');
                  if (icon) icon.style.transform = 'scale(1)';
                }}
              >
                <IconComponent className="feature-icon" style={iconStyle} />
                <h3 style={featureTitleStyle}>
                  {feature.title}
                </h3>
                <p style={featureDescStyle}>
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        <div style={whyChooseStyle}>
          <div style={whyChooseGridStyle}>
            <div>
              <h3 style={whyChooseTitleStyle}>
                Why Choose Hestia Doors?
              </h3>
              <ul style={listStyle}>
                <li style={listItemStyle}>
                  <span style={bulletStyle}></span>
                  <span>Premium quality exterior doors built to last</span>
                </li>
                <li style={listItemStyle}>
                  <span style={bulletStyle}></span>
                  <span>Decades of combined industry experience</span>
                </li>
                <li style={listItemStyle}>
                  <span style={bulletStyle}></span>
                  <span>Custom solutions for every project</span>
                </li>
                <li style={listItemStyle}>
                  <span style={bulletStyle}></span>
                  <span>Trusted by customers across Estonia</span>
                </li>
              </ul>
            </div>
            <div style={{ position: 'relative' }}>
              <div style={imageStyle}>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#9ca3af',
                  fontSize: '1rem'
                }}>
                  Modern Building Exterior
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
